import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PublicHeader from '../../../components/ui/PublicHeader';
import PublicFooter from '../../../components/ui/PublicFooter';
import { useAuth } from '../../../contexts/AuthContext';
import candidateService from '../../../services/candidateService';

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    interviewsScheduled: 0,
    profileCompletion: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch real data from the backend
      const [statsData, applicationsData] = await Promise.all([
        candidateService.getDashboardStats(),
        candidateService.getApplications()
      ]);

      // Set real stats data
      setStats({
        totalApplications: statsData?.total_applications || 0,
        pendingApplications: statsData?.pending_applications || 0,
        interviewsScheduled: statsData?.interviews_scheduled || 0,
        profileCompletion: Math.round(statsData?.profile_completion || 0)
      });

      // Set real applications data
      const apps = applicationsData?.applications || [];
      setApplications(Array.isArray(apps) ? apps : []);

    } catch (error) {
      console.error('Error loading dashboard:', error);
      setError('Failed to load dashboard data. Please try again later.');
      
      // Fallback to mock data only in development
      if (process.env.NODE_ENV === 'development') {
        setApplications([
          {
            id: 1,
            jobTitle: 'Frontend Developer',
            company: 'Tech Corp',
            appliedDate: '2024-01-15',
            status: 'under_review',
            location: 'Remote'
          },
          {
            id: 2,
            jobTitle: 'UX Designer',
            company: 'Design Studio',
            appliedDate: '2024-01-10',
            status: 'interview_scheduled',
            location: 'San Francisco'
          }
        ]);

        setStats({
          totalApplications: 2,
          pendingApplications: 1,
          interviewsScheduled: 1,
          profileCompletion: 75
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/public/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'uploaded': { color: 'bg-blue-100 text-blue-800', label: 'Application Submitted' },
      'processing': { color: 'bg-blue-100 text-blue-800', label: 'Under Review' },
      'completed': { color: 'bg-green-100 text-green-800', label: 'Review Complete' },
      'in_review': { color: 'bg-blue-100 text-blue-800', label: 'In Review' },
      'accepted': { color: 'bg-green-100 text-green-800', label: 'Accepted' },
      'rejected': { color: 'bg-red-100 text-red-800', label: 'Not Selected' },
      'interview_scheduled': { color: 'bg-green-100 text-green-800', label: 'Interview Scheduled' },
      'hired': { color: 'bg-green-100 text-green-800', label: 'Hired' }
    };
    
    // Map backend status to frontend status
    const mappedStatus = status?.toLowerCase().replace(' ', '_');
    const badge = badges[mappedStatus] || badges.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Shared Public Header */}
      <PublicHeader 
        currentUser={user}
        showAuthButtons={false}
        showNavigation={true}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} color="#dc2626" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={loadDashboardData}
                  className="mt-2 text-sm text-red-700 font-medium hover:text-red-800"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Icon name="FileText" size={24} color="#3b82f6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Icon name="Clock" size={24} color="#f59e0b" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingApplications}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Icon name="Calendar" size={24} color="#10b981" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Interviews</p>
                <p className="text-2xl font-bold text-gray-900">{stats.interviewsScheduled}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Icon name="User" size={24} color="#8b5cf6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Profile</p>
                <p className="text-2xl font-bold text-gray-900">{stats.profileCompletion}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              className="justify-start"
              variant="outline"
              onClick={() => navigate('/public/jobs')}
              iconName="Search"
            >
              Browse Available Jobs
            </Button>
            <Button
              className="justify-start"
              variant="outline"
              onClick={() => navigate('/public/track-application')}
              iconName="FileText"
            >
              Track Applications
            </Button>
            <Button
              className="justify-start"
              variant="outline"
              onClick={() => navigate('/candidate/profile')}
              iconName="Settings"
            >
              Update Profile
            </Button>
          </div>
        </div>

        {/* Recent Applications */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Applications</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/public/track-application')}
              >
                View All
              </Button>
            </div>
          </div>

          <div className="p-6">
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Briefcase" size={48} className="text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h4>
                <p className="text-gray-600 mb-4">
                  Start your job search by browsing our available positions.
                </p>
                <Button
                  onClick={() => navigate('/public/jobs')}
                  iconName="Search"
                >
                  Browse Jobs
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((application) => (
                  <div
                    key={application.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{application.job_title || application.jobTitle || 'Untitled Position'}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center space-x-1">
                            <Icon name="Building" size={14} />
                            <span>{application.company_name || application.company || 'Company Not Specified'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="MapPin" size={14} />
                            <span>{application.location || 'Location Not Specified'}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="Calendar" size={14} />
                            <span>
                              Applied {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : 
                                      application.appliedDate ? new Date(application.appliedDate).toLocaleDateString() : 
                                      'Unknown Date'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        {getStatusBadge(application.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Profile Completion Reminder */}
        {stats.profileCompletion < 100 && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <Icon name="Info" size={20} color="#3b82f6" className="mt-0.5" />
              <div className="ml-3 flex-1">
                <h4 className="text-sm font-medium text-blue-900">Complete Your Profile</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Your profile is {stats.profileCompletion}% complete. 
                  Complete your profile to improve your chances of being matched with relevant jobs.
                </p>
                <Button
                  className="mt-3"
                  size="sm"
                  onClick={() => navigate('/candidate/profile')}
                >
                  Complete Profile
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
};

export default CandidateDashboard;